/**
 * ECONOMIC PROFILES — TypeScript mirror of contracts/script/EconomicProfiles.sol.
 *
 * DeployLocal.s.sol (Anvil chain 31337 only) selects a profile with env `RUNNER_ECON_PROFILE` (default `FIXTURE_V1`)
 * and writes its id to the deployment manifest as `economicProfile`; the simulation-only production planner
 * (PrepareProduction.s.sol) selects one with `params.economicProfile`. NO PROFILE HERE IS APPROVED FOR PRODUCTION: the
 * status is either 'local-fixture' or 'study-candidate' and there is deliberately no 'approved' status.
 * LAUNCH_CANARY_V1 is the owner's choice for a testnet / private canary (docs/LAUNCH_CANARY.md), still not approved.
 *
 * test/profiles.test.ts parses the Solidity `Profile({...})` literals and compares them with this file field by field;
 * contracts/test/EconomicProfiles.t.sol checks FIXTURE_V1 == DefaultConfig.sol and deploys the system with every
 * profile. Quote amounts are micro-quote (`…Micro`: 1e-6 of one whole quote token): raw = micro × 10^decimals / 1e6.
 */
import { handlerParams, movementParams, shoeBehindBarrier, shoeBesideBarrier } from './config.js';
import type { FormationTemplate, HandlerParams, MovementParams, RoadParams, WaterfallPolicy } from './types.js';

export const ECONOMIC_PROFILE_IDS = [
  'FIXTURE_V1',
  'STUDY_CANDIDATE',
  'LAUNCH_CANARY_V1',
  'MAINNET_NKE_V1',
  'MAINNET_WETH_V1',
  'MAINNET_NKE_SMALL_V1',
  'MAINNET_WETH_SMALL_V1',
  'MAINNET_USD_SMALL_V1',
] as const;
export type EconomicProfileId = (typeof ECONOMIC_PROFILE_IDS)[number];
/** Never 'approved': production approval is outside this registry. */
export type EconomicProfileStatus = 'local-fixture' | 'study-candidate';

/** The profile DeployLocal uses when `RUNNER_ECON_PROFILE` is unset (keeps every existing test, vector and e2e). */
export const DEFAULT_ECONOMIC_PROFILE: EconomicProfileId = 'FIXTURE_V1';

/** The caveat every surface shows next to a profile label. */
export const ECONOMIC_PROFILE_CAVEAT = 'LOCAL/TEST only, not approved for production';
/** The launch canary's caveat: it is meant for a real (test or canary) chain, so "LOCAL/TEST" would be wrong. */
export const CANARY_PROFILE_CAVEAT = 'CANARY only (testnet or private canary), not audited, not approved for production';

/** The owner's mainnet profiles: chosen by the owner on 2026-10-10, still without independent (audit/legal) approval. */
export const MAINNET_PROFILE_CAVEAT = 'MAINNET launch profile chosen by the owner; not audited, not approved for production by any independent review';
const MAINNET_PROFILE_IDS: readonly string[] = ['MAINNET_NKE_V1', 'MAINNET_WETH_V1', 'MAINNET_NKE_SMALL_V1', 'MAINNET_WETH_SMALL_V1', 'MAINNET_USD_SMALL_V1'];

/** The caveat for a profile id: the canary's or the mainnet profiles' own, else the LOCAL/TEST one (also for ids this
 *  build does not know). */
export function economicProfileCaveat(id: string): string {
  if (id === 'LAUNCH_CANARY_V1') return CANARY_PROFILE_CAVEAT;
  if (MAINNET_PROFILE_IDS.includes(id)) return MAINNET_PROFILE_CAVEAT;
  return ECONOMIC_PROFILE_CAVEAT;
}

/** Short tag shown before the label: CANARY, MAINNET, or LOCAL/TEST. */
export function economicProfileTag(id: string | null | undefined): 'CANARY' | 'MAINNET' | 'LOCAL/TEST' {
  if (id === 'LAUNCH_CANARY_V1') return 'CANARY';
  if (id && MAINNET_PROFILE_IDS.includes(id)) return 'MAINNET';
  return 'LOCAL/TEST';
}

export interface EconomicProfile {
  id: EconomicProfileId;
  label: string;
  status: EconomicProfileStatus;
  /** RunnerHook fee (bps of the gross quote leg); immutable at hook deployment, the hook accepts 1–100. */
  hookFeeBps: bigint;
  /** RunnerTreasury initial waterfall (bps of R; the five buckets sum to 10,000). */
  holdersBps: bigint;
  forgeBps: bigint;
  roadBps: bigint;
  prizeBps: bigint;
  opsBps: bigint;
  /** Deferred part of the holder allocation (bps of the holder allocation). */
  holderDeferredBps: bigint;
  /** RoadParams (micro-quote amounts). */
  runBudgetMicro: bigint;
  minRunBudgetMicro: bigint;
  opsProvisionMicro: bigint;
  waterSliceBps: bigint;
  overheatSliceBps: bigint;
  overheatBiasSteps: bigint;
  /** Fixture: runBudget × overheatSliceBps × overheatBiasSteps / 10,000 (0.6 of 2 quote). */
  overheatCapMicro: bigint;
  /** HandlerParams Golden Shoe prize (micro-quote). */
  shoePrizeMicro: bigint;
  shoeMinPrizeMicro: bigint;
  /** RunnerBuyback.Limits caps (micro-quote) and the cap period. */
  buybackPerTradeCapMicro: bigint;
  buybackPeriodCapMicro: bigint;
  buybackPeriodSeconds: bigint;
  /** MovementParams.sensitivity. */
  sensitivity: bigint;
  /** Golden Shoe formation in the course set: 1 = shoeBehindBarrier (fixture), 2 = shoeBesideBarrier (collectible). */
  shoeFormation: bigint;
}

/** FIXTURE_V1 — exactly today's DefaultConfig.sol / DeployLocal.s.sol values (labelled local fixture). */
export const FIXTURE_V1: Readonly<EconomicProfile> = Object.freeze({
  id: 'FIXTURE_V1',
  label: 'Local fixture v1 (DefaultConfig)',
  status: 'local-fixture',
  hookFeeBps: 50n,
  holdersBps: 3000n,
  forgeBps: 2000n,
  roadBps: 2000n,
  prizeBps: 1000n,
  opsBps: 2000n,
  holderDeferredBps: 3000n,
  runBudgetMicro: 2_000_000n,
  minRunBudgetMicro: 200_000n,
  opsProvisionMicro: 50_000n,
  waterSliceBps: 2500n,
  overheatSliceBps: 1000n,
  overheatBiasSteps: 3n,
  overheatCapMicro: 600_000n,
  shoePrizeMicro: 500_000n,
  shoeMinPrizeMicro: 100_000n,
  buybackPerTradeCapMicro: 5_000_000n,
  buybackPeriodCapMicro: 20_000_000n,
  buybackPeriodSeconds: 3600n,
  sensitivity: 1n,
  shoeFormation: 1n,
} as const);

/**
 * STUDY_CANDIDATE — the economic study's provisional candidate C14 (docs/economics/RECOMMENDATION.md). LOCAL/TEST ONLY,
 * not approved: the hook fee is conditional on blocker B3 (platform obligation) and on measured fee elasticity. Keep it
 * identical to contracts/script/EconomicProfiles.sol `studyCandidate()`; the parity test enforces it.
 */
export const STUDY_CANDIDATE: Readonly<EconomicProfile> = Object.freeze({
  id: 'STUDY_CANDIDATE',
  label: 'Economic study candidate C14 (30/15/20/5/30, 20 bps) - LOCAL/TEST only, not approved',
  status: 'study-candidate',
  hookFeeBps: 20n,
  holdersBps: 3000n,
  forgeBps: 1500n,
  roadBps: 2000n,
  prizeBps: 500n,
  opsBps: 3000n,
  holderDeferredBps: 3000n,
  runBudgetMicro: 1_000_000n,
  minRunBudgetMicro: 100_000n,
  opsProvisionMicro: 50_000n,
  waterSliceBps: 2500n,
  overheatSliceBps: 1000n,
  overheatBiasSteps: 3n,
  overheatCapMicro: 300_000n,
  shoePrizeMicro: 1_000_000n,
  shoeMinPrizeMicro: 200_000n,
  buybackPerTradeCapMicro: 5_000_000n,
  buybackPeriodCapMicro: 10_000_000n,
  buybackPeriodSeconds: 3600n,
  sensitivity: 1n,
  shoeFormation: 1n,
} as const);

/**
 * LAUNCH_CANARY_V1 — the owner's launch choice for a testnet / private canary (docs/LAUNCH_CANARY.md): study candidate
 * C15 = C14 at 50 bps (the fee is immutable and blocker B3 is unresolved), with the collectible Golden Shoe formation.
 * Exhibition runs stay OFF (minRunBudget > 0). Not audited (B8); not approved for mainnet. Keep it identical to
 * contracts/script/EconomicProfiles.sol `launchCanaryV1()`; the parity test enforces it.
 */
export const LAUNCH_CANARY_V1: Readonly<EconomicProfile> = Object.freeze({
  id: 'LAUNCH_CANARY_V1',
  label: 'Launch canary C15 (30/15/20/5/30, 50 bps, collectible shoe) - testnet/canary only, not audited',
  status: 'study-candidate',
  hookFeeBps: 50n,
  holdersBps: 3000n,
  forgeBps: 1500n,
  roadBps: 2000n,
  prizeBps: 500n,
  opsBps: 3000n,
  holderDeferredBps: 3000n,
  runBudgetMicro: 1_000_000n,
  minRunBudgetMicro: 100_000n,
  opsProvisionMicro: 50_000n,
  waterSliceBps: 2500n,
  overheatSliceBps: 1000n,
  overheatBiasSteps: 3n,
  overheatCapMicro: 300_000n,
  shoePrizeMicro: 1_000_000n,
  shoeMinPrizeMicro: 200_000n,
  buybackPerTradeCapMicro: 5_000_000n,
  buybackPeriodCapMicro: 10_000_000n,
  buybackPeriodSeconds: 3600n,
  sensitivity: 1n,
  shoeFormation: 2n,
} as const);

/**
 * MAINNET_NKE_V1 — the owner's mainnet choice of 2026-10-10 (docs/MAINNET_READINESS.md, D51): study candidate C14
 * (30/15/20/5/30, 20 bps) with the collectible Golden Shoe formation and the prize share on, exhibition runs OFF, quoted in
 * a Nike-linked ERC-20. Owner-selected, not audited (B8), legal review open (B7). Keep it identical to
 * contracts/script/EconomicProfiles.sol `mainnetNkeV1()`; the parity test enforces it.
 */
export const MAINNET_NKE_V1: Readonly<EconomicProfile> = Object.freeze({
  id: 'MAINNET_NKE_V1',
  label: 'Mainnet C14 (30/15/20/5/30, 20 bps, collectible shoe), Nike-linked quote - owner-selected, not audited',
  status: 'study-candidate',
  hookFeeBps: 20n,
  holdersBps: 3000n,
  forgeBps: 1500n,
  roadBps: 2000n,
  prizeBps: 500n,
  opsBps: 3000n,
  holderDeferredBps: 3000n,
  runBudgetMicro: 1_000_000n,
  minRunBudgetMicro: 100_000n,
  opsProvisionMicro: 50_000n,
  waterSliceBps: 2500n,
  overheatSliceBps: 1000n,
  overheatBiasSteps: 3n,
  overheatCapMicro: 300_000n,
  shoePrizeMicro: 1_000_000n,
  shoeMinPrizeMicro: 200_000n,
  buybackPerTradeCapMicro: 5_000_000n,
  buybackPeriodCapMicro: 10_000_000n,
  buybackPeriodSeconds: 3600n,
  sensitivity: 1n,
  shoeFormation: 2n,
} as const);

/**
 * MAINNET_WETH_V1 — MAINNET_NKE_V1 re-denominated for a WETH quote (no verified Nike-linked ERC-20 on 4663 yet): every
 * quote amount divided by 40 (the same USD value at an assumed ETH 3,000 / NKE 75 USD), everything else identical.
 * Keep it identical to contracts/script/EconomicProfiles.sol `mainnetWethV1()`.
 */
export const MAINNET_WETH_V1: Readonly<EconomicProfile> = Object.freeze({
  id: 'MAINNET_WETH_V1',
  label: 'Mainnet C14 (30/15/20/5/30, 20 bps, collectible shoe), WETH quote (amounts /40) - owner-selected, not audited',
  status: 'study-candidate',
  hookFeeBps: 20n,
  holdersBps: 3000n,
  forgeBps: 1500n,
  roadBps: 2000n,
  prizeBps: 500n,
  opsBps: 3000n,
  holderDeferredBps: 3000n,
  runBudgetMicro: 25_000n,
  minRunBudgetMicro: 2_500n,
  opsProvisionMicro: 1_250n,
  waterSliceBps: 2500n,
  overheatSliceBps: 1000n,
  overheatBiasSteps: 3n,
  overheatCapMicro: 7_500n,
  shoePrizeMicro: 25_000n,
  shoeMinPrizeMicro: 5_000n,
  buybackPerTradeCapMicro: 125_000n,
  buybackPeriodCapMicro: 250_000n,
  buybackPeriodSeconds: 3600n,
  sensitivity: 1n,
  shoeFormation: 2n,
} as const);

/**
 * MAINNET_NKE_SMALL_V1 — MAINNET_NKE_V1 for the single-sided small launch (D53): every quote amount divided by 125, for a
 * start price 125× below the studied one (8e-7 instead of 1e-4 quote per RUNNER). Prices and quote amounts scale
 * together, so every RUNNER quantity (steering, pace, deposits) is the studied one; fixed costs (gas, randomness fees)
 * do not scale. Hook fee 100 bps instead of 20 (owner 2026-10-10, D59). Keep it identical to
 * contracts/script/EconomicProfiles.sol `mainnetNkeSmallV1()`.
 */
export const MAINNET_NKE_SMALL_V1: Readonly<EconomicProfile> = Object.freeze({
  id: 'MAINNET_NKE_SMALL_V1',
  label: 'Mainnet C14 (30/15/20/5/30, 100 bps, collectible shoe), Nike-linked quote, small launch (amounts /125) - owner-selected, not audited',
  status: 'study-candidate',
  hookFeeBps: 100n,
  holdersBps: 3000n,
  forgeBps: 1500n,
  roadBps: 2000n,
  prizeBps: 500n,
  opsBps: 3000n,
  holderDeferredBps: 3000n,
  runBudgetMicro: 8_000n,
  minRunBudgetMicro: 800n,
  opsProvisionMicro: 3_300n, // D66
  waterSliceBps: 2500n,
  overheatSliceBps: 1000n,
  overheatBiasSteps: 3n,
  overheatCapMicro: 2_400n,
  shoePrizeMicro: 8_000n,
  shoeMinPrizeMicro: 1_600n,
  buybackPerTradeCapMicro: 40_000n,
  buybackPeriodCapMicro: 80_000n,
  buybackPeriodSeconds: 3600n,
  sensitivity: 1n,
  shoeFormation: 2n,
} as const);

/**
 * MAINNET_WETH_SMALL_V1 — MAINNET_WETH_V1 for the single-sided small launch (D53): every quote amount divided by 125, for
 * a start price of 2e-8 WETH per RUNNER (FDV 20 WETH) instead of the studied 2.5e-6 (D58 starts 12x lower again and
 * compensates with the game's reference supply). Same scaling argument, caveat and 100 bps hook fee (D59) as
 * MAINNET_NKE_SMALL_V1. Keep it identical to contracts/script/EconomicProfiles.sol `mainnetWethSmallV1()`.
 */
export const MAINNET_WETH_SMALL_V1: Readonly<EconomicProfile> = Object.freeze({
  id: 'MAINNET_WETH_SMALL_V1',
  label: 'Mainnet C14 (30/15/20/5/30, 100 bps, collectible shoe), WETH quote, small launch (amounts /40/125) - owner-selected, not audited',
  status: 'study-candidate',
  hookFeeBps: 100n,
  holdersBps: 3000n,
  forgeBps: 1500n,
  roadBps: 2000n,
  prizeBps: 500n,
  opsBps: 3000n,
  holderDeferredBps: 3000n,
  runBudgetMicro: 200n,
  minRunBudgetMicro: 20n,
  opsProvisionMicro: 80n, // D66
  waterSliceBps: 2500n,
  overheatSliceBps: 1000n,
  overheatBiasSteps: 3n,
  overheatCapMicro: 60n,
  shoePrizeMicro: 200n,
  shoeMinPrizeMicro: 40n,
  buybackPerTradeCapMicro: 1_000n,
  buybackPeriodCapMicro: 2_000n,
  buybackPeriodSeconds: 3600n,
  sensitivity: 1n,
  shoeFormation: 2n,
} as const);

/**
 * MAINNET_USD_SMALL_V1 — the small launch (D53) quoted in a 1-USD stablecoin such as USDG (D55): the same USD amounts as
 * MAINNET_WETH_SMALL_V1 at ETH 3,000 and MAINNET_NKE_SMALL_V1 at NKE 75, for any USD stablecoin whatever its decimals.
 * Hook fee 100 bps (D59). Keep it identical to contracts/script/EconomicProfiles.sol `mainnetUsdSmallV1()`.
 */
export const MAINNET_USD_SMALL_V1: Readonly<EconomicProfile> = Object.freeze({
  id: 'MAINNET_USD_SMALL_V1',
  label: 'Mainnet C14 (30/15/20/5/30, 100 bps, collectible shoe), USD stablecoin quote, small launch - owner-selected, not audited',
  status: 'study-candidate',
  hookFeeBps: 100n,
  holdersBps: 3000n,
  forgeBps: 1500n,
  roadBps: 2000n,
  prizeBps: 500n,
  opsBps: 3000n,
  holderDeferredBps: 3000n,
  runBudgetMicro: 600_000n,
  minRunBudgetMicro: 60_000n,
  opsProvisionMicro: 250_000n, // D66
  waterSliceBps: 2500n,
  overheatSliceBps: 1000n,
  overheatBiasSteps: 3n,
  overheatCapMicro: 180_000n,
  shoePrizeMicro: 600_000n,
  shoeMinPrizeMicro: 120_000n,
  buybackPerTradeCapMicro: 3_000_000n,
  buybackPeriodCapMicro: 6_000_000n,
  buybackPeriodSeconds: 3600n,
  sensitivity: 1n,
  shoeFormation: 2n,
} as const);

export const ECONOMIC_PROFILES: Readonly<Record<EconomicProfileId, Readonly<EconomicProfile>>> = Object.freeze({
  FIXTURE_V1,
  STUDY_CANDIDATE,
  LAUNCH_CANARY_V1,
  MAINNET_NKE_V1,
  MAINNET_WETH_V1,
  MAINNET_NKE_SMALL_V1,
  MAINNET_WETH_SMALL_V1,
  MAINNET_USD_SMALL_V1,
});

export function isEconomicProfileId(id: unknown): id is EconomicProfileId {
  return typeof id === 'string' && (ECONOMIC_PROFILE_IDS as readonly string[]).includes(id);
}

/** The profile with this id; throws for anything else (DeployLocal refuses unknown ids the same way). */
export function economicProfile(id: string): Readonly<EconomicProfile> {
  if (!isEconomicProfileId(id)) throw new Error(`unknown economic profile "${id}" (known: ${ECONOMIC_PROFILE_IDS.join(', ')})`);
  return ECONOMIC_PROFILES[id];
}

/** "Economic profile: <label> — LOCAL/TEST only, not approved for production" (the canary's own caveat for
 *  LAUNCH_CANARY_V1, or the summary's own note). */
export function economicProfileText(p: { id?: string; label: string; note?: string }): string {
  return `Economic profile: ${p.label} — ${p.note ?? economicProfileCaveat(p.id ?? '')}`;
}

/** micro-quote → raw quote units for a token with `decimals` decimals (rounds down, as EconomicProfiles.quoteUnits). */
export function microQuoteUnits(micro: bigint, decimals: number): bigint {
  return (micro * 10n ** BigInt(decimals)) / 1_000_000n;
}

export function profileWaterfall(p: EconomicProfile): WaterfallPolicy {
  return {
    holdersBps: p.holdersBps,
    forgeBps: p.forgeBps,
    roadBps: p.roadBps,
    prizeBps: p.prizeBps,
    opsBps: p.opsBps,
    holderDeferredBps: p.holderDeferredBps,
  };
}

export function profileRoad(p: EconomicProfile, quoteDecimals = 18): RoadParams {
  return {
    runBudget: microQuoteUnits(p.runBudgetMicro, quoteDecimals),
    minRunBudget: microQuoteUnits(p.minRunBudgetMicro, quoteDecimals),
    opsProvision: microQuoteUnits(p.opsProvisionMicro, quoteDecimals),
    waterSliceBps: p.waterSliceBps,
    overheatSliceBps: p.overheatSliceBps,
    overheatBiasSteps: p.overheatBiasSteps,
    overheatCap: microQuoteUnits(p.overheatCapMicro, quoteDecimals),
  };
}

/** DefaultConfig handlers with the profile's Golden Shoe prize. */
export function profileHandlers(p: EconomicProfile, quoteDecimals = 18): HandlerParams {
  return {
    ...handlerParams(quoteDecimals),
    shoePrize: microQuoteUnits(p.shoePrizeMicro, quoteDecimals),
    shoeMinPrize: microQuoteUnits(p.shoeMinPrizeMicro, quoteDecimals),
  };
}

/** DefaultConfig movement with the profile's input sensitivity. */
export function profileMovement(p: EconomicProfile): MovementParams {
  return { ...movementParams(), sensitivity: p.sensitivity };
}

/** The profile's Golden Shoe formation (formation 2 of the course set); throws for an unknown selector. */
export function profileShoeFormation(p: EconomicProfile): FormationTemplate {
  if (p.shoeFormation === 1n) return shoeBehindBarrier();
  if (p.shoeFormation === 2n) return shoeBesideBarrier();
  throw new Error(`economic profile ${p.id}: unknown shoeFormation ${p.shoeFormation}`);
}

/** RunnerBuyback.Limits; TWAP 300 s / 300 ticks / 300 bps are execution guards, identical in every profile. */
export interface ProfileBuybackLimits {
  perTradeCap: bigint;
  periodCap: bigint;
  periodSeconds: bigint;
  twapSeconds: bigint;
  maxDeviationTicks: bigint;
  maxSlippageBps: bigint;
}

export function profileBuybackLimits(p: EconomicProfile, quoteDecimals = 18): ProfileBuybackLimits {
  return {
    perTradeCap: microQuoteUnits(p.buybackPerTradeCapMicro, quoteDecimals),
    periodCap: microQuoteUnits(p.buybackPeriodCapMicro, quoteDecimals),
    periodSeconds: p.buybackPeriodSeconds,
    twapSeconds: 300n,
    maxDeviationTicks: 300n,
    maxSlippageBps: 300n,
  };
}

/**
 * The checks the contracts apply to a profile's values (RunnerHook constructor, WaterfallLib.validate,
 * EncounterRegistry.validateConfig for the profile's fields, RunnerBuyback._setLimits). Empty = deployable.
 */
export function economicProfileViolations(p: EconomicProfile): string[] {
  const out: string[] = [];
  const bad = (cond: boolean, msg: string) => {
    if (cond) out.push(msg);
  };
  bad(p.hookFeeBps < 1n || p.hookFeeBps > 100n, 'hookFeeBps outside 1–100 (RunnerHook)');
  bad(p.holdersBps + p.forgeBps + p.roadBps + p.prizeBps + p.opsBps !== 10_000n, 'waterfall buckets do not sum to 10,000 bps');
  bad(p.holderDeferredBps > 10_000n, 'holderDeferredBps > 10,000');
  bad(p.runBudgetMicro === 0n || p.minRunBudgetMicro > p.runBudgetMicro, 'road budget: runBudget = 0 or minRunBudget > runBudget');
  bad(p.waterSliceBps > 10_000n || p.overheatSliceBps > 10_000n, 'road slices > 10,000 bps');
  bad(p.overheatBiasSteps === 0n || p.overheatBiasSteps > 6n, 'overheatBiasSteps outside 1–6');
  bad(p.shoeMinPrizeMicro > p.shoePrizeMicro, 'shoeMinPrize > shoePrize');
  bad(p.sensitivity === 0n || p.sensitivity > 1_000_000n, 'sensitivity outside 1–1,000,000');
  bad(p.buybackPeriodSeconds === 0n || p.buybackPerTradeCapMicro > p.buybackPeriodCapMicro, 'buyback: period 0 or perTradeCap > periodCap');
  bad(p.shoeFormation !== 1n && p.shoeFormation !== 2n, 'shoeFormation is neither 1 (behind barrier) nor 2 (beside barrier)');
  return out;
}

/** What a deployment reports about its profile (API `economicProfile`, web label). Never approved. */
export interface EconomicProfileSummary {
  id: string;
  label: string;
  /** 'unknown': the manifest names a profile this build does not know. */
  status: EconomicProfileStatus | 'unknown';
  approvedForProduction: false;
  note: string;
}

/**
 * The profile a deployment manifest names (`economicProfile`, written by DeployLocal). A local (31337) manifest without
 * the field predates profiles and was deployed with DefaultConfig, which FIXTURE_V1 equals field by field; a non-local
 * manifest without it has no profile (null). An id this build does not know is reported as 'unknown', never relabelled.
 */
export function economicProfileSummary(manifest: { chainId: number | string; economicProfile?: string | null }): EconomicProfileSummary | null {
  const raw = manifest.economicProfile;
  const id = raw === undefined || raw === null ? '' : String(raw);
  if (id === '') {
    if (Number(manifest.chainId) !== 31337) return null;
    const f = FIXTURE_V1;
    return {
      id: f.id,
      label: f.label,
      status: f.status,
      approvedForProduction: false,
      note: `${ECONOMIC_PROFILE_CAVEAT} (manifest predates economic profiles; deployed with DefaultConfig = FIXTURE_V1)`,
    };
  }
  if (isEconomicProfileId(id)) {
    const p = ECONOMIC_PROFILES[id];
    return { id: p.id, label: p.label, status: p.status, approvedForProduction: false, note: economicProfileCaveat(p.id) };
  }
  return {
    id,
    label: `Unknown economic profile ${id}`,
    status: 'unknown',
    approvedForProduction: false,
    note: `${ECONOMIC_PROFILE_CAVEAT} (this build does not know the profile named in the manifest)`,
  };
}
